import { MUEmailGrey, MUWebsite, MUWhatsappGrey } from '../../../assets/hostedassets';
import styles from '../MagicUpload.module.scss';
import { IS_COMPANY, MAGIC_UPLOAD, sourceMap } from './MagicUploadConstants';

export type DropdownOption = { value: string; label: string };

/** Returns undefined for null, empty, or literal "undefined" strings. */
export const validOrgId = (v?: string | null): string | undefined => (v && v !== 'undefined' ? v : undefined);

/**
 * Resolves the correct assesseeOrgId based on context.
 * In company context (isCompany or isBorrowerReq), URL is the source of truth
 * because Redux holds the partner's own org ID, not the target company's.
 */
export const resolveAssesseeOrgId = ({
    isCompany,
    isBorrowerReq,
    orgIdFromQuery,
    orgIdFromRoute,
    orgIdFromRedux,
}: {
    isCompany: boolean;
    isBorrowerReq: boolean;
    orgIdFromQuery: string | null;
    orgIdFromRoute: string | undefined;
    orgIdFromRedux: string | undefined;
}): string | undefined => {
    return isCompany || isBorrowerReq
        ? validOrgId(orgIdFromQuery) ?? validOrgId(orgIdFromRoute) ?? validOrgId(orgIdFromRedux)
        : validOrgId(orgIdFromRedux) ?? validOrgId(orgIdFromRoute);
};

/**
 * Builds the duplicate-file toast content as a structured labeled block.
 * Names the Data Room folder in addition to the company when `folderPath` is
 * available (RECUR-27331); the folder row is omitted entirely when absent/empty.
 */
export const buildDuplicateFileMessage = (fileName: string, orgName: string, folderPath?: string): JSX.Element => (
    <div className={styles.duplicateToast}>
        <div className={styles.duplicateToastHeader}>Duplicate file</div>
        <span className={styles.duplicateToastLabel}>File:</span>
        <span title={fileName} className={styles.duplicateToastFileName}>
            {fileName}
        </span>
        <span className={styles.duplicateToastLabel}>Company:</span>
        <span className={styles.duplicateToastValue}>{orgName}</span>
        {folderPath && (
            <>
                <span className={styles.duplicateToastLabel}>Folder:</span>
                <span className={styles.duplicateToastFolder}>{folderPath}</span>
            </>
        )}
    </div>
);

/**
 * Sets the three params that open the company-scoped Magic Upload drawer with the
 * Data Requests tab available (mirrors Topbar's "Upload Data" click), and clears
 * stale `needInfo`/`magicSign` so an earlier drawer visit doesn't hijack the
 * landing tab (precedent: useMagicUploadLogic.openCompanyMagicUpload).
 *
 * Pure: returns a new URLSearchParams, callers own the setSearchParams call.
 */
export const applyBorrowerMagicUploadParams = (params: URLSearchParams): URLSearchParams => {
    const nextParams = new URLSearchParams(params);
    nextParams.set(MAGIC_UPLOAD, 'true');
    nextParams.set(IS_COMPANY, 'true');
    nextParams.set('showNeedInfo', 'true');
    nextParams.delete('needInfo');
    nextParams.delete('magicSign');
    return nextParams;
};

export type SourceType = 'whats_app' | 'gmail' | 'app' | 'ask_aica';

export const getSourceIcon = (source: SourceType) => {
    switch (source) {
        case 'whats_app':
            return <img src={MUWhatsappGrey} alt={'Whatsapp'} />;
        case 'gmail':
            return <img style={{ height: '22px' }} src={MUEmailGrey} alt={'Email'} />;
        case 'app':
        case 'ask_aica':
            return <img src={MUWebsite} alt={'Website'} />;
        default:
            return '-';
    }
};

/**
 * Maps a backend source key to its display label, falling back to the raw
 * source when it isn't in sourceMap. Prevents an undefined label from reaching
 * the filter (sourceMap only covers a fixed set of known sources).
 */
export const resolveSourceLabel = (source: string): string => (sourceMap as Record<string, string>)[source] ?? source;

/**
 * Case-insensitive filter of dropdown options by search text. Guards against a
 * null/undefined label — backend-derived labels (org name, mapped source, user
 * name) can be missing, and an unguarded .toLowerCase() crashes the render.
 */
export const filterOptionsBySearch = (options: DropdownOption[], searchText: string): DropdownOption[] => {
    const query = (searchText ?? '').toLowerCase();
    return options.filter((option) => (option.label ?? '').toLowerCase().includes(query));
};
